/**
 * =============================================================================
 * rpcRetry.ts — Retry con backoff esponenziale per chiamate RPC Supabase
 * =============================================================================
 *
 * Wrapper sottile attorno a una promise che ne ritenta l'esecuzione in caso
 * di errori "transitori" (rete, timeout, 5xx). NON ritenta in caso di errori
 * applicativi (codici prefissati come SAME_SECTION, CYCLE_DETECTED, ecc.):
 * quegli errori sono deterministici e ripeterli non cambia l'esito.
 *
 * Uso tipico:
 *   const result = await withRpcRetry(() => supabase.rpc("execute_page_move", args));
 *   if (result.error) throw result.error;
 *
 * IMPORTANTE: l'idempotenza dell'RPC è garantita da `operation_token`
 * (Blocco 1+2). È quindi sicuro ripetere la chiamata anche se la prima è
 * effettivamente arrivata al server: il secondo tentativo verrà riconosciuto
 * come replay e non duplicherà lo spostamento.
 * =============================================================================
 */

/** Codici di errore applicativi che NON devono essere ritentati. */
const NON_RETRYABLE_PREFIXES = [
  "SAME_SECTION",
  "CROSS_COURSE",
  "PAGE_NOT_IN_SOURCE",
  "CYCLE_DETECTED",
  "PERMISSION_DENIED",
  "SOURCE_NOT_FOUND",
  "DEST_NOT_FOUND",
  "COURSE_MISMATCH",
  "HISTORY_NOT_FOUND",
  "ALREADY_UNDONE",
  "SANITY_FAILED",
];

function isRetryable(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as any)?.message ?? String(err);
  // Errori applicativi noti: non ritentare.
  for (const p of NON_RETRYABLE_PREFIXES) {
    if (msg.includes(p)) return false;
  }
  // Codici postgres permanenti tipici (constraint violation, syntax, ecc.):
  // li riconosciamo via campo `code` se presente.
  const code = (err as any)?.code;
  if (typeof code === "string" && /^(22|23|42)/.test(code)) return false;
  // Tutto il resto (network, 5xx, timeout) è considerato transitorio.
  return true;
}

export interface RpcRetryOptions {
  /** Numero massimo di tentativi (incluso il primo). Default 3. */
  maxAttempts?: number;
  /** Ritardo iniziale in ms. Raddoppia ad ogni tentativo. Default 300ms. */
  baseDelayMs?: number;
  /** Callback opzionale di osservabilità: `(attempt, err) => void`. */
  onRetry?: (attempt: number, err: unknown) => void;
}

/**
 * Esegue `fn` ritentando con backoff esponenziale (300ms, 600ms, 1200ms…)
 * in caso di errori transitori. Restituisce sempre l'ultimo risultato.
 */
export async function withRpcRetry<T>(
  fn: () => PromiseLike<T>,
  opts: RpcRetryOptions = {},
): Promise<T> {
  const max = opts.maxAttempts ?? 3;
  const base = opts.baseDelayMs ?? 300;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= max; attempt++) {
    try {
      const res = (await fn()) as T;
      // Supabase restituisce { data, error } anche su success → propaga errore
      // applicativo se presente, lasciando al chiamante decidere.
      const maybeErr = (res as any)?.error;
      if (maybeErr && isRetryable(maybeErr) && attempt < max) {
        lastErr = maybeErr;
        opts.onRetry?.(attempt, maybeErr);
        await sleep(base * Math.pow(2, attempt - 1));
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (!isRetryable(err) || attempt === max) throw err;
      opts.onRetry?.(attempt, err);
      await sleep(base * Math.pow(2, attempt - 1));
    }
  }
  // Unreachable se max>=1, ma per type-safety.
  throw lastErr ?? new Error("withRpcRetry: nessun tentativo eseguito");
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}